import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw, FileText, Upload, Check, Trash2, AlertCircle } from 'lucide-react'
import { apiClient } from '@/lib/api'

const LAST_CONV_KEY = 'miniapp_last_conversation_id'
const SELECTED_DOCS_KEY = 'miniapp_selected_doc_ids'
const PROCESSING_STATUSES = new Set(['initiated', 'uploaded', 'pending', 'parsing', 'indexing', 'processing'])

type DocumentItem = {
  id: string
  filename: string
  scope?: string
  contract_id?: string | null
  conversation_id?: string | null
  size_bytes?: number
  mime?: string
  status?: string
  error_message?: string | null
  created_at?: string
}

function normalizeSelectedDocIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.map((item) => String(item || '').trim()).filter(Boolean)
}

function humanizeDocError(raw?: string | null): string | null {
  const message = String(raw || '').trim()
  if (!message) return null
  const lowered = message.toLowerCase()
  if (lowered.includes('429 too many requests') && lowered.includes('embeddings')) {
    return 'Сервис индексации временно перегружен. Повторите позже.'
  }
  if (lowered.includes('500 internal server error') && lowered.includes('embeddings')) {
    return 'Сервис индексации временно недоступен. Повторите позже.'
  }
  if (lowered.includes('401 unauthorized') && lowered.includes('embeddings')) {
    return 'Ошибка авторизации сервиса индексации. Повторите позже.'
  }
  if (lowered.includes('not a valid point id')) {
    return 'Ошибка индексации документа. Перезагрузите документ.'
  }
  if (lowered.includes('timeout') || lowered.includes('timed out')) {
    return 'Превышено время обработки документа. Повторите позже.'
  }
  return message.length > 180 ? `${message.slice(0, 177)}...` : message
}

function statusLabel(status?: string): string {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'ready') return 'Готов'
  if (normalized === 'error') return 'Ошибка'
  if (normalized === 'pending') return 'В очереди'
  if (normalized === 'initiated') return 'Запущено'
  if (normalized === 'uploaded') return 'Загружен'
  if (normalized === 'parsing') return 'Парсится'
  if (normalized === 'indexing') return 'Индексируется'
  if (!normalized) return 'Неизвестно'
  return 'Обработка'
}

function formatDocDateTime(value?: string): string | null {
  const source = String(value || '').trim()
  if (!source) return null
  const date = new Date(source)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function DocumentsPage() {
  const [docs, setDocs] = useState<DocumentItem[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>(() => {
    try {
      return normalizeSelectedDocIds(JSON.parse(localStorage.getItem(SELECTED_DOCS_KEY) || '[]'))
    } catch {
      return []
    }
  })
  const [conversationId, setConversationId] = useState<string | undefined>(() => {
    try { return localStorage.getItem(LAST_CONV_KEY) || undefined } catch { return undefined }
  })
  const [contractId, setContractId] = useState('')
  const [acceptAttr, setAcceptAttr] = useState('.pdf,.docx,.txt')
  const [maxUploadMb, setMaxUploadMb] = useState(25)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const loadDocs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.getDocuments({
        page: 1,
        pageSize: 200,
      })
      const items = Array.isArray(response.items) ? response.items : []
      setDocs(items)
      const selectableIds = new Set(
        items
          .filter((item) => {
            const ready = String(item.status || '').toLowerCase() === 'ready'
            if (!ready) return false
            const isChatScope = String(item.scope || '').toLowerCase() === 'chat'
            if (!isChatScope) return true
            if (!conversationId) return false
            return String(item.conversation_id || '').trim() === String(conversationId).trim()
          })
          .map((item) => String(item.id || '').trim())
          .filter(Boolean),
      )
      setSelectedDocIds((prev) => prev.filter((id) => selectableIds.has(id)))
    } catch (err) {
      setDocs([])
      setError(err instanceof Error ? err.message : 'Ошибка загрузки документов')
    } finally {
      setLoading(false)
    }
  }, [conversationId])

  useEffect(() => {
    try {
      const currentConversationId = localStorage.getItem(LAST_CONV_KEY) || undefined
      setConversationId(currentConversationId)
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    void loadDocs()
  }, [loadDocs])

  useEffect(() => {
    const loadCapabilities = async () => {
      try {
        const capabilities = await apiClient.getDocumentsCapabilities()
        const item = capabilities?.item || {}
        if (Array.isArray(item.allowed_extensions) && item.allowed_extensions.length > 0) {
          setAcceptAttr(item.allowed_extensions.join(','))
        }
        if (typeof item.max_upload_mb === 'number') {
          setMaxUploadMb(item.max_upload_mb)
        }
      } catch {
        // fallback
      }
    }
    void loadCapabilities()
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem(SELECTED_DOCS_KEY, JSON.stringify(selectedDocIds))
    } catch {
      // ignore
    }
  }, [selectedDocIds])

  const hasProcessingDocs = useMemo(
    () => docs.some((doc) => PROCESSING_STATUSES.has(String(doc.status || '').toLowerCase())),
    [docs],
  )

  useEffect(() => {
    if (!hasProcessingDocs) return
    const timer = window.setInterval(() => {
      void loadDocs()
    }, 2500)
    return () => window.clearInterval(timer)
  }, [hasProcessingDocs, loadDocs])

  useEffect(() => {
    if (!activeJobId) return
    const timer = window.setInterval(async () => {
      try {
        const job = await apiClient.getDocumentsJob(activeJobId)
        const status = String(job?.item?.status || '').toLowerCase()
        await loadDocs()
        if (status === 'completed' || status === 'failed') {
          setActiveJobId(null)
        }
      } catch {
        await loadDocs()
      }
    }, 2000)
    return () => window.clearInterval(timer)
  }, [activeJobId, loadDocs])

  const toggleDoc = (doc: DocumentItem) => {
    const docId = String(doc.id || '').trim()
    if (!docId) return
    if (String(doc.status || '').toLowerCase() !== 'ready') return
    const isChatScope = String(doc.scope || '').toLowerCase() === 'chat'
    if (isChatScope && (!conversationId || String(doc.conversation_id || '').trim() !== String(conversationId).trim())) {
      return
    }
    if (selectedDocIds.includes(docId)) {
      setSelectedDocIds(selectedDocIds.filter((id) => id !== docId))
    } else {
      setSelectedDocIds([...selectedDocIds, docId])
    }
  }

  const uploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    setError(null)
    try {
      const result = await apiClient.uploadDocuments(Array.from(files), contractId.trim() || undefined)
      const jobId = String(result?.item?.job_id || '').trim()
      if (jobId) setActiveJobId(jobId)
      await loadDocs()
      if (inputRef.current) inputRef.current.value = ''
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки документов')
    } finally {
      setUploading(false)
    }
  }

  const deleteDoc = async (docId: string) => {
    if (!docId || deletingDocId) return
    setDeletingDocId(docId)
    setError(null)
    try {
      await apiClient.deleteDocument(docId)
      if (selectedDocIds.includes(docId)) {
        setSelectedDocIds(selectedDocIds.filter((id) => id !== docId))
      }
      await loadDocs()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка удаления документа')
    } finally {
      setDeletingDocId(null)
    }
  }

  return (
    <div className="documents-page flex flex-col h-full overflow-hidden">
      <div className="documents-header sticky top-0 z-10 glass-header" style={{ padding: '10px 16px' }}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="font-bold" style={{ fontSize: '14px' }}>Документы</h1>
            <p style={{ fontSize: '10px', color: 'var(--header-hint-color)' }}>
              Выбрано для чата: {selectedDocIds.length}
            </p>
          </div>
          <button
            onClick={() => void loadDocs()}
            className="rounded-lg glass-icon flex items-center gap-1 px-2 active:scale-95 transition-transform"
            style={{ height: '32px' }}
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} style={{ color: 'var(--header-hint-color)' }} />
            <span className="text-[10px] font-medium" style={{ color: 'var(--header-hint-color)' }}>Обновить</span>
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto" style={{ padding: '12px 16px calc(12px + var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}>
        <div className="glass-card" style={{ padding: '12px', marginBottom: '10px' }}>
          <p className="font-semibold" style={{ fontSize: '12px', marginBottom: '8px' }}>Загрузка документов</p>
          <div className="flex flex-col gap-2">
            <input
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              placeholder="Contract ID (опционально)"
              className="glass-input text-sm"
              style={{ padding: '9px 12px' }}
            />
            <input
              ref={inputRef}
              type="file"
              accept={acceptAttr}
              multiple
              className="glass-input text-sm"
              style={{ padding: '9px 12px' }}
              disabled={uploading}
              onChange={(e) => void uploadFiles(e.target.files)}
            />
            <p className="text-[10px] text-telegram-hint">Разрешено: {acceptAttr} • до {maxUploadMb} MB</p>
            {activeJobId && (
              <p className="text-[10px] text-telegram-button">Загрузка принята, документ индексируется…</p>
            )}
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="rounded-xl bg-telegram-button text-white flex items-center justify-center transition-all active:scale-95 disabled:opacity-40"
              style={{ height: '38px', gap: '6px' }}
            >
              <Upload size={14} />
              <span style={{ fontSize: '12px', fontWeight: 600 }}>{uploading ? 'Загрузка...' : 'Выбрать файлы'}</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="glass-surface flex items-start gap-2" style={{ padding: '10px 12px', marginBottom: '10px' }}>
            <AlertCircle size={14} className="text-red-500 shrink-0" />
            <p className="text-xs text-red-500 break-words">{error}</p>
          </div>
        )}

        {!loading && docs.length === 0 && (
          <div className="glass-surface flex items-center justify-center gap-2" style={{ padding: '18px 12px' }}>
            <FileText size={16} className="text-telegram-hint" />
            <p className="text-xs text-telegram-hint">Документы не найдены</p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          {docs.map((doc) => {
            const docId = String(doc.id || '').trim()
            const selected = selectedDocIds.includes(docId)
            const status = String(doc.status || '').toLowerCase()
            const ready = status === 'ready'
            const deleting = deletingDocId === docId
            const uploadedAt = formatDocDateTime(doc.created_at)
            const isChatScope = String(doc.scope || '').toLowerCase() === 'chat'
            const selectableInChat = !isChatScope || (!!conversationId && String(doc.conversation_id || '').trim() === String(conversationId).trim())
            const canSelect = ready && selectableInChat
            return (
              <div key={docId} className="glass-surface" style={{ padding: '10px 12px' }}>
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => toggleDoc(doc)}
                    disabled={!canSelect}
                    className="shrink-0 rounded-lg flex items-center justify-center transition-all active:scale-95 disabled:opacity-40"
                    style={{
                      width: '24px',
                      height: '24px',
                      border: selected ? 'none' : '1px solid var(--glass-border)',
                      background: selected ? 'var(--tg-theme-button-color, #2481cc)' : 'transparent',
                      color: selected ? 'var(--tg-theme-button-text-color, #fff)' : 'var(--tg-theme-hint-color, #999)',
                    }}
                    title={canSelect ? 'Выбрать документ' : 'Можно выбрать только готовый документ текущего чата'}
                  >
                    {selected ? <Check size={13} /> : null}
                  </button>

                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate" style={{ fontSize: '12px' }}>{doc.filename}</p>
                    <p className="text-[10px] text-telegram-hint">
                      {doc.scope === 'chat' ? 'Чат' : doc.scope === 'contract' ? 'Контракт' : 'Глобально'}
                      {typeof doc.size_bytes === 'number' ? ` • ${Math.round(doc.size_bytes / 1024)} KB` : ''}
                      {uploadedAt ? ` • ${uploadedAt}` : ''}
                    </p>
                    {isChatScope && !selectableInChat && (
                      <p className="text-[10px] text-telegram-hint">Выбор доступен только внутри чата, где документ был загружен.</p>
                    )}
                    {humanizeDocError(doc.error_message) && (
                      <p className="text-[10px] text-red-500 truncate">{humanizeDocError(doc.error_message)}</p>
                    )}
                  </div>

                  <div className="shrink-0 flex items-center gap-1">
                    <span
                      className="rounded-full px-2 py-0.5"
                      style={{
                        fontSize: '9px',
                        background: ready ? 'var(--tg-theme-button-color, #2481cc)' : 'rgba(127,127,127,0.2)',
                        color: ready ? 'var(--tg-theme-button-text-color, #fff)' : 'var(--tg-theme-hint-color, #999)',
                      }}
                    >
                      {statusLabel(doc.status)}
                    </span>
                    <button
                      onClick={() => void deleteDoc(docId)}
                      disabled={!docId || deleting}
                      className="rounded-lg flex items-center justify-center active:scale-90 transition-transform text-telegram-hint hover:text-red-500 disabled:opacity-40"
                      style={{ width: '24px', height: '24px' }}
                      title="Удалить"
                    >
                      <Trash2 size={12} className={deleting ? 'animate-pulse' : ''} />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
